import React from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Text,
    useStyleConfig,
    useDisclosure,
    List,
    ListItem
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {WeChatModal} from '../../../components/wechat-modal'
import {Component} from '@salesforce/commerce-sdk-react/components'
import WeChatButton from '../../../components/footer/wechat-button'

import {useGTM} from '@app/hooks/use-gtm'

/**
 * PDCFooterLinksListDesktop component renders a list of footer links for desktop.
 *
 * @param {string} props.title - The title to display above the list of links.
 * @param {Array} props.regions - An array of region objects containing components to render.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const PDCFooterLinksListDesktop = ({regions, title}) => {
    const components = regions?.[0]?.components || []
    const {isOpen: isWeChatOpen, onOpen: onWeChatOpen, onClose: onWeChatClose} = useDisclosure()
    const {triggerFooterLinkEvent} = useGTM()
    const styles = useStyleConfig('Footer')
    const linkMarginBottom = {
        '&:not(:last-of-type)': {
            marginBottom: ' 2px'
        }
    }

    /**
     * Handles the click event for footer link items in the desktop footer links list.
     *
     * This function is typically used as an event handler to process user interactions
     * with footer links, such as navigation or analytics tracking, within the
     * Page Designer Component (PDC) footer on desktop layouts.
     *
     * @param {React.MouseEvent} event - The click event object triggered by the user.
     * @returns {void}
     */
    const handleOnClick = (e, label, onWeChatOpen) => {
        const redirectURL = e.target?.href || ''
        // Add a 50ms delay before executing the event logic
        setTimeout(() => {
            triggerFooterLinkEvent(redirectURL, label)

            // Trigger the onWeChatOpen event if provided
            if (onWeChatOpen) {
                onWeChatOpen()
            }
        }, 50)
    }
    return (
        <Box>
            <Text
                role={'heading'}
                aria-level={2}
                variant={'bodyBase2'}
                fontSize={10}
                fontWeight={500}
                mb={'20px'}
                textTransform={'uppercase'}
            >
                {title}
            </Text>
            <List role={undefined}>
                {components.map((link, index) => {
                    const {fontColor = styles.link.color, fontSize = '12px', label} = link.data
                    const isWeChat = label?.toLowerCase() === 'wechat'

                    return (
                        <ListItem key={index} sx={linkMarginBottom}>
                            {isWeChat ? (
                                <React.Fragment>
                                    <WeChatButton
                                        fontColor={fontColor}
                                        fontSize={fontSize}
                                        label={label}
                                        onWeChatOpen={(e) =>
                                            handleOnClick(e, link, label, onWeChatOpen)
                                        }
                                    />
                                    <WeChatModal isOpen={isWeChatOpen} onClose={onWeChatClose} />
                                </React.Fragment>
                            ) : (
                                <Component
                                    component={{
                                        ...link,
                                        fontColor,
                                        fontSize,
                                        textDecoration: 'none',
                                        display: 'inline-block',
                                        onClick: (e) => handleOnClick(e, link, label)
                                    }}
                                />
                            )}
                        </ListItem>
                    )
                })}
            </List>
        </Box>
    )
}

export default PDCFooterLinksListDesktop

PDCFooterLinksListDesktop.propTypes = {
    title: PropTypes.string.isRequired,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
